import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  message,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
} from "antd";
import { PlusOutlined, SaveOutlined, MinusCircleOutlined } from "@ant-design/icons";
import { Link } from "react-router-dom";
import EditIcon from "../../assets/images/EditIcon";
import DeleteIcon from "../../assets/images/DeleteIcon";
import NodataFoundIcon from "../../assets/images/NodataFoundIcon";
import Service from "../../service";
import { useDispatch } from "react-redux";
import { hideAuthLoader, showAuthLoader } from "../../appRedux/actions/Auth";
import { PAGE_SIZE_OPTIONS } from "../../constants/tablePagination";
import {
  expenseApi,
  formatMoney,
  getExpenseCurrencySymbol,
  setExpenseCurrencySymbol,
  SALARY_GRADES,
} from "./expenseHelpers";
import approvalApi from "../../api/approvalApi";
import { getResource } from "../../util/getResourcePermission";
import config from "../../permission/config.json";
import "./expenseManagement.css";

const { TabPane } = Tabs;
const { Option } = Select;

// Approval-workflow builder options.
const WORKFLOW_ROLES = [
  "User",
  "Reporting Manager",
  "Assistance Reporting Manager",
  "Account Team",
  "HR",
  "Admin",
];
const WORKFLOW_ACTIONS = ["submit", "approve"];
const DEFAULT_WORKFLOW_STEPS = [
  { roleName: "User", action: "submit" },
  { roleName: "Reporting Manager", action: "approve" },
  { roleName: "Account Team", action: "approve" },
];
// Roles that can be granted issue / settle rights for advances.
const ADVANCE_ROLE_OPTIONS = [
  "HR",
  "Account Team",
  "Reporting Manager",
  "Assistance Reporting Manager",
  "Admin",
];
const DEFAULT_ISSUER_ROLES = ["HR", "Account Team", "Reporting Manager"];
const DEFAULT_SETTLER_ROLES = ["Account Team"];

const canCreate = () => getResource(config.RESOURCES.EXPENSE_CONFIG, ["create"]);
const canUpdate = () => getResource(config.RESOURCES.EXPENSE_CONFIG, ["update"]);
const canDelete = () => getResource(config.RESOURCES.EXPENSE_CONFIG, ["delete"]);

// ── Cost Center master CRUD ─────────────────────────────────────────────────
const CostCenterTab = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [modal, setModal] = useState({ open: false, editing: null });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });
  const [total, setTotal] = useState(0);

  const mayCreate = useMemo(() => canCreate(), []);
  const mayDelete = useMemo(() => canDelete(), []);

  const load = useCallback(async () => {
    dispatch(showAuthLoader());
    try {
      const data = await expenseApi.get(Service.expenseCostCenter, {
        page: pagination.current,
        limit: pagination.pageSize,
      });
      setItems(data?.items || data || []);
      setTotal(data?.total || (Array.isArray(data) ? data.length : 0));
    } catch (err) {
      message.error(err.message || "Failed to load cost centers");
    } finally {
      dispatch(hideAuthLoader());
    }
  }, [dispatch, pagination.current, pagination.pageSize]);

  useEffect(() => {
    load();
  }, [load]);

  const openModal = (editing) => {
    form.resetFields();
    if (editing) form.setFieldsValue(editing);
    setModal({ open: true, editing });
  };

  const save = async (values) => {
    dispatch(showAuthLoader());
    try {
      if (modal.editing) {
        await expenseApi.put(`${Service.expenseCostCenter}/${modal.editing._id}`, values);
        message.success("Cost center updated");
      } else {
        await expenseApi.post(Service.expenseCostCenter, values);
        message.success("Cost center created");
      }
      setModal({ open: false, editing: null });
      load();
    } catch (err) {
      message.error(err.message || "Save failed");
    } finally {
      dispatch(hideAuthLoader());
    }
  };

  const remove = async (id) => {
    dispatch(showAuthLoader());
    try {
      await expenseApi.del(`${Service.expenseCostCenter}/${id}`);
      message.success("Cost center deleted");
      load();
    } catch (err) {
      message.error(err.message || "Delete failed");
    } finally {
      dispatch(hideAuthLoader());
    }
  };

  const columns = [
    { title: "Name", dataIndex: "name", key: "name" },
    { title: "Code", dataIndex: "code", key: "code", render: (v) => v || "-" },
    { title: "Description", dataIndex: "description", key: "description", render: (v) => v || "-" },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="link"
              className="edit"
              onClick={() => openModal(record)}
            >
              <EditIcon />
            </Button>
          </Tooltip>
          {mayDelete && (
            <Popconfirm title="Delete this cost center?" onConfirm={() => remove(record._id)}>
              <Tooltip title="Delete">
                <Button type="link" className="delete">
                  <DeleteIcon />
                </Button>
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="search-wrapper">
        <div className="filter-btns-wrapper">
          {mayCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openModal(null)}
            >
              Cost Center
            </Button>
          )}
        </div>
      </div>
      <div className="main-table-wrapper">
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={items}
          locale={{ emptyText: <NodataFoundIcon /> }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            onChange: (current, pageSize) => setPagination({ current, pageSize }),
          }}
          scroll={{ x: "max-content" }}
        />
      </div>
      <Modal
        title={modal.editing ? "Edit Cost Center" : "Add Cost Center"}
        open={modal.open}
        onCancel={() => setModal({ open: false, editing: null })}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={save}>
          <Form.Item name="name" label="Name" rules={[{ required: true, message: "Enter a name" }]}>
            <Input placeholder="Cost center name" />
          </Form.Item>
          <Form.Item name="code" label="Code">
            <Input placeholder="Cost center code" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} placeholder="Description" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

// ── Expense Limit CRUD (grade x dept x category -> amount) ───────────────────
const ExpenseLimitTab = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [items, setItems] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [designations, setDesignations] = useState([]);
  const [modal, setModal] = useState({ open: false, editing: null });
  const [pagination, setPagination] = useState({ current: 1, pageSize: 25 });
  const [total, setTotal] = useState(0);

  const mayCreate = useMemo(() => canCreate(), []);
  const mayDelete = useMemo(() => canDelete(), []);

  const load = useCallback(async () => {
    dispatch(showAuthLoader());
    try {
      const data = await expenseApi.get(Service.expenseLimit, {
        page: pagination.current,
        limit: pagination.pageSize,
      });
      setItems(data?.items || data || []);
      setTotal(data?.total || (Array.isArray(data) ? data.length : 0));
    } catch (err) {
      message.error(err.message || "Failed to load limits");
    } finally {
      dispatch(hideAuthLoader());
    }
  }, [dispatch, pagination.current, pagination.pageSize]);

  const loadDepartments = useCallback(async () => {
    try {
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.empdepartmentDropdownList,
      });
      setDepartments(response?.data?.data || []);
    } catch {
      /* departments optional */
    }
  }, []);

  const loadDesignations = useCallback(async () => {
    try {
      const response = await Service.makeAPICall({
        methodName: Service.postMethod,
        api_url: Service.empdesignationDropdownList,
      });
      setDesignations(response?.data?.data || []);
    } catch {
      /* designations optional */
    }
  }, []);

  useEffect(() => {
    load();
    loadDepartments();
    loadDesignations();
  }, [load, loadDepartments, loadDesignations]);

  const openModal = (editing) => {
    form.resetFields();
    if (editing) {
      form.setFieldsValue({
        salaryGrade: editing.salaryGrade,
        designationId: editing.designationId,
        departmentId: editing.departmentId,
        category: editing.category,
        limitAmount: editing.limitAmount,
        period: editing.period || "Monthly",
      });
    } else {
      form.setFieldsValue({ period: "Monthly" });
    }
    setModal({ open: true, editing });
  };

  const save = async (values) => {
    dispatch(showAuthLoader());
    try {
      const limitAmount =
        values.limitAmount === undefined || values.limitAmount === null
          ? null
          : values.limitAmount;

      if (modal.editing) {
        // Edit targets a single existing row (single-select in edit mode).
        await expenseApi.put(`${Service.expenseLimit}/${modal.editing._id}`, {
          salaryGrade: values.salaryGrade || null,
          designationId: values.designationId || null,
          departmentId: values.departmentId || null,
          category: values.category,
          limitAmount,
          period: values.period || "Monthly",
        });
        message.success("Limit updated");
      } else {
        // On create, Department/Designation are multi-select. Create one limit per
        // department × designation combination (empty = "all", stored as null).
        const departmentIds = values.departmentId?.length ? values.departmentId : [null];
        const designationIds = values.designationId?.length ? values.designationId : [null];
        const combinations = [];
        departmentIds.forEach((departmentId) =>
          designationIds.forEach((designationId) =>
            combinations.push({ departmentId, designationId })
          )
        );
        await Promise.all(
          combinations.map((combo) =>
            expenseApi.post(Service.expenseLimit, {
              salaryGrade: values.salaryGrade || null,
              designationId: combo.designationId,
              departmentId: combo.departmentId,
              category: values.category,
              limitAmount,
              period: values.period || "Monthly",
            })
          )
        );
        message.success(
          combinations.length > 1
            ? `${combinations.length} limits created`
            : "Limit created"
        );
      }
      setModal({ open: false, editing: null });
      load();
    } catch (err) {
      message.error(err.message || "Save failed");
    } finally {
      dispatch(hideAuthLoader());
    }
  };

  const remove = async (id) => {
    dispatch(showAuthLoader());
    try {
      await expenseApi.del(`${Service.expenseLimit}/${id}`);
      message.success("Limit deleted");
      load();
    } catch (err) {
      message.error(err.message || "Delete failed");
    } finally {
      dispatch(hideAuthLoader());
    }
  };

  const deptName = (id) =>
    departments.find((d) => d._id === id)?.department_name || "All";
  const desigName = (id) =>
    designations.find((d) => d._id === id)?.designation_name || "All";

  const columns = [
    {
      title: "Grade",
      dataIndex: "salaryGrade",
      key: "salaryGrade",
      render: (v) => v || "All",
    },
    {
      title: "Designation",
      dataIndex: "designationId",
      key: "designationId",
      render: (v) => desigName(v),
    },
    {
      title: "Department",
      dataIndex: "departmentId",
      key: "departmentId",
      render: (v) => deptName(v),
    },
    { title: "Category", dataIndex: "category", key: "category" },
    {
      title: "Limit",
      dataIndex: "limitAmount",
      key: "limitAmount",
      render: (v) => (v === null || v === undefined ? "Unlimited" : formatMoney(v)),
    },
    {
      title: "Period",
      dataIndex: "period",
      key: "period",
      render: (v) => <Tag color="blue">{v || "Monthly"}</Tag>,
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          <Tooltip title="Edit">
            <Button
              type="link"
              className="edit"
              onClick={() => openModal(record)}
            >
              <EditIcon />
            </Button>
          </Tooltip>
          {mayDelete && (
            <Popconfirm title="Delete this limit?" onConfirm={() => remove(record._id)}>
              <Tooltip title="Delete">
                <Button type="link" className="delete">
                  <DeleteIcon />
                </Button>
              </Tooltip>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <>
      <div className="search-wrapper">
        <div className="filter-btns-wrapper">
          {mayCreate && (
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => openModal(null)}
            >
              Limit
            </Button>
          )}
        </div>
      </div>
      <div className="main-table-wrapper">
        <Table
          rowKey="_id"
          columns={columns}
          dataSource={items}
          locale={{ emptyText: <NodataFoundIcon /> }}
          pagination={{
            current: pagination.current,
            pageSize: pagination.pageSize,
            total,
            showSizeChanger: true,
            pageSizeOptions: PAGE_SIZE_OPTIONS,
            onChange: (current, pageSize) => setPagination({ current, pageSize }),
          }}
          scroll={{ x: "max-content" }}
        />
      </div>
      <Modal
        title={modal.editing ? "Edit Limit" : "Add Limit"}
        open={modal.open}
        onCancel={() => setModal({ open: false, editing: null })}
        onOk={() => form.submit()}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={save}>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="salaryGrade" label="Grade (blank = all)">
                <Select allowClear placeholder="Select grade">
                  {SALARY_GRADES.map((g) => (
                    <Option key={g} value={g}>
                      {g}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="departmentId" label="Department (blank = all)">
                <Select
                  mode={modal.editing ? undefined : "multiple"}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                  placeholder="Select department"
                >
                  {departments.map((d) => (
                    <Option key={d._id} value={d._id}>
                      {d.department_name}
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
          </Row>
          <Form.Item name="designationId" label="Designation (blank = all)">
            <Select
              mode={modal.editing ? undefined : "multiple"}
              allowClear
              showSearch
              optionFilterProp="children"
              placeholder="Select designation"
            >
              {designations.map((d) => (
                <Option key={d._id} value={d._id}>
                  {d.designation_name}
                </Option>
              ))}
            </Select>
          </Form.Item>
          <Form.Item
            name="category"
            label="Category"
            rules={[{ required: true, message: "Enter a category" }]}
          >
            <Input placeholder="Expense category" />
          </Form.Item>
          <Row gutter={16}>
            <Col span={12}>
              <Form.Item name="limitAmount" label="Limit Amount (blank = unlimited)">
                <InputNumber min={0} style={{ width: "100%" }} prefix={<span style={{ marginRight: 4 }}>{getExpenseCurrencySymbol()}</span>} placeholder="Unlimited" />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="period"
                label="Period"
                tooltip="The limit is a cumulative cap over this window: total spend in the category per Day / Week / Month."
              >
                <Select placeholder="Select period">
                  <Option value="Daily">Daily</Option>
                  <Option value="Weekly">Weekly</Option>
                  <Option value="Monthly">Monthly</Option>
                </Select>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Modal>
    </>
  );
};

// ── Expense Config (singleton per company) ──────────────────────────────────
const ExpenseConfigTab = () => {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  // EXPENSE can now also run on the central Approval Engine (its own builder
  // at /admin/approval-workflow/EXPENSE). Once a company switches it on,
  // `workflowSteps` below is dead - getWorkflowSteps() server-side stops
  // reading it - so this card is disabled with a link out rather than left
  // editable and silently ignored.
  const [engineEnabled, setEngineEnabled] = useState(false);
  const companySlug = localStorage.getItem("companyDomain");

  const mayUpdate = useMemo(() => canUpdate(), []);

  const load = useCallback(async () => {
    setLoading(true);
    dispatch(showAuthLoader());
    try {
      const data = await expenseApi.get(Service.expenseConfig);
      approvalApi
        .getWorkflowByModule("EXPENSE")
        .then((res) => setEngineEnabled(Boolean(res?.data?.engineEnabled)))
        .catch(() => setEngineEnabled(false));
      form.setFieldsValue({
        categories: data?.categories || [],
        maxAdvanceAmount: data?.maxAdvanceAmount ?? null,
        settlementDays: data?.settlementDays ?? 15,
        mandatoryBillThreshold: data?.mandatoryBillThreshold ?? null,
        settleBeforeNewAdvance: data?.settleBeforeNewAdvance ?? true,
        issuerRoles: data?.issuerRoles?.length ? data.issuerRoles : DEFAULT_ISSUER_ROLES,
        settlerRoles: data?.settlerRoles?.length ? data.settlerRoles : DEFAULT_SETTLER_ROLES,
        autoApprovalRules: {
          enabled: data?.autoApprovalRules?.enabled ?? false,
          maxAmount: data?.autoApprovalRules?.maxAmount ?? 0,
        },
        policies: data?.policies || "",
        exceptionApproverRole: data?.exceptionApproverRole ?? "HR",
        currencySymbol: data?.currencySymbol || "₹",
        workflowSteps: (data?.workflowSteps?.length
          ? data.workflowSteps
          : DEFAULT_WORKFLOW_STEPS
        ).map((step) => ({ roleName: step.roleName, action: step.action })),
      });
      setExpenseCurrencySymbol(data?.currencySymbol || "₹");
    } catch (err) {
      message.error(err.message || "Failed to load configuration");
    } finally {
      setLoading(false);
      dispatch(hideAuthLoader());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const save = async (values) => {
    dispatch(showAuthLoader());
    try {
      // Build the ordered workflow from the visual step rows (order = position).
      const workflowSteps = (values.workflowSteps || [])
        .filter((step) => step && step.roleName && step.action)
        .map((step, index) => ({
          order: index + 1,
          roleName: step.roleName,
          action: step.action,
        }));
      const body = {
        categories: values.categories || [],
        maxAdvanceAmount: values.maxAdvanceAmount ?? null,
        settlementDays: values.settlementDays ?? 15,
        mandatoryBillThreshold: values.mandatoryBillThreshold ?? null,
        settleBeforeNewAdvance: values.settleBeforeNewAdvance,
        issuerRoles: values.issuerRoles || [],
        settlerRoles: values.settlerRoles || [],
        autoApprovalRules: {
          enabled: values.autoApprovalRules?.enabled ?? false,
          maxAmount: values.autoApprovalRules?.maxAmount ?? 0,
        },
        policies: values.policies || "",
        exceptionApproverRole: values.exceptionApproverRole || "",
        currencySymbol: values.currencySymbol || "₹",
        ...(workflowSteps.length ? { workflowSteps } : {}),
      };
      await expenseApi.put(Service.expenseConfig, body);
      setExpenseCurrencySymbol(body.currencySymbol);
      message.success("Configuration saved");
    } catch (err) {
      message.error(err.message || "Save failed");
    } finally {
      dispatch(hideAuthLoader());
    }
  };

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={save}
      disabled={loading}
      className="expense-config-form"
    >
      <Card
        type="inner"
        size="small"
        title="General Settings"
        className="expense-config-card"
      >
        <Form.Item
          name="categories"
          label="Expense Categories"
          tooltip="The categories employees choose from when raising advances and submitting expenses."
        >
          <Select mode="tags" placeholder="Type a category and press Enter" />
        </Form.Item>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="maxAdvanceAmount"
              label="Max Advance Amount"
              tooltip="Largest advance that can be issued. Blank = no cap."
            >
              <InputNumber min={0} style={{ width: "100%" }} prefix={<span style={{ marginRight: 4 }}>{getExpenseCurrencySymbol()}</span>} placeholder="No cap" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="settlementDays"
              label="Settlement Days"
              tooltip="Days within which an advance should be settled; drives the due-for-closure reminder."
            >
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="mandatoryBillThreshold"
              label="Mandatory Bill Threshold"
              tooltip="When an expense amount is greater than this value, the employee MUST attach a bill/receipt (JPG/PNG/PDF) or the submission is blocked. Amounts at or below it need no attachment. Leave blank to never require a bill."
            >
              <InputNumber min={0} style={{ width: "100%" }} prefix={<span style={{ marginRight: 4 }}>{getExpenseCurrencySymbol()}</span>} placeholder="None" />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="settleBeforeNewAdvance"
              label="Require settlement before new advance"
              valuePropName="checked"
              tooltip="Block issuing a new advance to an employee who still has an open one."
              className="expense-config-nomargin"
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="currencySymbol"
              label="Currency Symbol"
              tooltip="Shown across the expense module (advances, expenses, reports, dashboard)."
              className="expense-config-nomargin"
            >
              <Select showSearch placeholder="Select currency">
                {["₹", "$", "€", "£", "¥", "₩", "﷼", "₽", "₺", "₴"].map((sym) => (
                  <Option key={sym} value={sym}>
                    {sym}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card
        type="inner"
        size="small"
        title="Approvals & Automation"
        className="expense-config-card"
      >
        <Row gutter={16}>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name={["autoApprovalRules", "enabled"]}
              label="Enable Auto-Approval"
              valuePropName="checked"
              tooltip="Auto-approve compliant expenses at or below the max amount. Over-limit exceptions never auto-approve."
            >
              <Switch />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name={["autoApprovalRules", "maxAmount"]}
              label="Auto-Approval Max Amount"
            >
              <InputNumber min={0} style={{ width: "100%" }} prefix={<span style={{ marginRight: 4 }}>{getExpenseCurrencySymbol()}</span>} placeholder="0" />
            </Form.Item>
          </Col>
          <Col xs={24} sm={12} md={8}>
            <Form.Item
              name="exceptionApproverRole"
              label="Over-limit Extra Approver"
              tooltip={
                engineEnabled
                  ? "This module runs on the Approval Engine now - the conditional approver step is edited from Approval Workflow > Expense instead."
                  : "Over-limit (exception) expenses require ONE extra approval by this role, added before the final approval step. Blank = none."
              }
            >
              <Select allowClear placeholder="Select role (e.g. HR)" disabled={engineEnabled}>
                {["HR", "Admin", "Account Team", "Reporting Manager", "Assistance Reporting Manager"].map(
                  (role) => (
                    <Option key={role} value={role}>
                      {role}
                    </Option>
                  )
                )}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <Form.Item
              name="issuerRoles"
              label="Roles allowed to issue advances"
              tooltip="Only these roles can issue an advance to an employee. Applies to the backend and hides the Issue button for other roles."
            >
              <Select mode="multiple" allowClear placeholder="Select roles">
                {ADVANCE_ROLE_OPTIONS.map((role) => (
                  <Option key={role} value={role}>
                    {role}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item
              name="settlerRoles"
              label="Roles allowed to settle advances"
              tooltip="Only these roles can settle an advance (Admin can always settle). Also controls whether the Settle button shows."
            >
              <Select mode="multiple" allowClear placeholder="Select roles">
                {ADVANCE_ROLE_OPTIONS.map((role) => (
                  <Option key={role} value={role}>
                    {role}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
      </Card>

      <Card
        type="inner"
        size="small"
        title="Expense Policy"
        className="expense-config-card"
      >
        <Form.Item
          name="policies"
          label="Policy text shown to employees"
          className="expense-config-nomargin"
        >
          <Input.TextArea
            rows={4}
            placeholder="e.g. All travel bills above ₹2,000 require a receipt…"
          />
        </Form.Item>
      </Card>

      <Card
        type="inner"
        size="small"
        title="Approval Workflow"
        className="expense-config-card"
      >
        {engineEnabled ? (
          <Alert
            type="info"
            showIcon
            message="Managed in Approval Workflow settings now"
            description={
              <>
                This module now runs on the central Approval Engine, so the steps below are
                no longer used - edits here will not change approval behaviour. Configure the
                chain from{" "}
                <Link to={`/${companySlug}/admin/approval-workflow/EXPENSE`}>
                  Approval Workflow &gt; Expense
                </Link>
                .
              </>
            }
            style={{ marginBottom: 16 }}
          />
        ) : null}
        <div className="workflow-steps-fieldset">
          <p className="workflow-help">
                Steps run top to bottom. <strong>submit</strong> = the employee raising the
                expense, <strong>approve</strong> = a review/approval step.
              </p>
              <Form.List name="workflowSteps">
                {(fields, { add, remove }) => (
                  <>
                    {fields.map((field, index) => (
                      <div className="workflow-step-row" key={field.key}>
                        <span className="workflow-step-order">{index + 1}</span>
                        <Form.Item
                          name={[field.name, "roleName"]}
                          fieldKey={[field.fieldKey, "roleName"]}
                          rules={[{ required: true, message: "Select a role" }]}
                          className="workflow-step-field"
                        >
                          <Select placeholder="Role" showSearch optionFilterProp="children" disabled={engineEnabled}>
                            {WORKFLOW_ROLES.map((role) => (
                              <Option key={role} value={role}>
                                {role}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Form.Item
                          name={[field.name, "action"]}
                          fieldKey={[field.fieldKey, "action"]}
                          rules={[{ required: true, message: "Select an action" }]}
                          className="workflow-step-field"
                        >
                          <Select placeholder="Action" disabled={engineEnabled}>
                            {WORKFLOW_ACTIONS.map((action) => (
                              <Option key={action} value={action}>
                                {action}
                              </Option>
                            ))}
                          </Select>
                        </Form.Item>
                        <Button
                          type="text"
                          danger
                          icon={<MinusCircleOutlined />}
                          onClick={() => remove(field.name)}
                          className="workflow-step-remove"
                          aria-label="Remove step"
                          disabled={engineEnabled}
                        />
                      </div>
                    ))}
                    <Button
                      type="dashed"
                      onClick={() => add({ action: "approve" })}
                      icon={<PlusOutlined />}
                      block
                      disabled={engineEnabled}
                    >
                      Add Step
                    </Button>
                  </>
                )}
              </Form.List>
        </div>
      </Card>

      {mayUpdate && (
        <div className="expense-config-actions">
          <Button type="primary" htmlType="submit" icon={<SaveOutlined />}>
            Save Configuration
          </Button>
        </div>
      )}
    </Form>
  );
};

// Admin config page - Cost Center master, Expense Limits, and Expense Config.
// Route + menu are gated to Admin / HR via EXPENSE_CONFIG resource.
const ExpenseConfigAdmin = () => (
  <Card className="employee-card expense-page">
    <div className="inout-header text-left">
      <h2>Expense Configuration</h2>
    </div>
    <Card className="main-content-wrapper">
      <Tabs defaultActiveKey="costCenter">
        <TabPane tab="Cost Centers" key="costCenter">
          <CostCenterTab />
        </TabPane>
        <TabPane tab="Expense Limits" key="limits">
          <ExpenseLimitTab />
        </TabPane>
        <TabPane tab="Configuration" key="config">
          <ExpenseConfigTab />
        </TabPane>
      </Tabs>
    </Card>
  </Card>
);

export default ExpenseConfigAdmin;
